import React, { useEffect, useRef, useState } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import EditorAddIcon from '@atlaskit/icon/glyph/editor/add';
import Select, {
	type OptionProps,
	type InputActionMeta,
	components,
	type MenuListComponentProps,
} from '@atlaskit/select';
import { colors } from '@atlaskit/theme';
// eslint-disable-next-line import/order
import { token } from '@atlaskit/tokens';

import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { TicketType } from '../../../common/types';
import useGetRequestFormOptions from '../../../controllers/get-request-form-options/index.tsx';
import useUpdateTicketTypesInGroup from '../../../controllers/update-ticket-types-in-group/index.tsx';
import RequestFormItemContainer from './item-container';
import messages from './messages';

type Props = {
	groupId: number;
};

const getOptionLabel = ({ name, description }: TicketType) => `${name} ${description}`;

const Option = ({ data, innerProps, innerRef, isFocused }: OptionProps<TicketType>) => (
	<SelectItem {...innerProps} ref={innerRef} isFocused={isFocused}>
		<RequestFormItemContainer ticketType={data} />
	</SelectItem>
);

const MenuList = ({ innerRef, ...rest }: MenuListComponentProps<TicketType>) => {
	const listRef = useRef<unknown>();

	useEffect(() => {
		// Scrolls the menu back into view when options change
		setTimeout(() => {
			listRef.current &&
				// @ts-expect-error - TS2571 - Object is of type 'unknown'.
				listRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
		});
	}, [rest.options]);

	return (
		<components.MenuList
			{...rest}
			innerRef={(ref) => {
				// @ts-expect-error - TS2349 - This expression is not callable. | TS2721 - Cannot invoke an object which is possibly 'null'.
				innerRef(ref);
				listRef.current = ref;
			}}
		/>
	);
};

const RequestForm = ({ groupId }: Props) => {
	const { formatMessage } = useIntl();
	const [shouldShowSelect, setShowMenu] = useState<boolean>(false);
	const { add } = useUpdateTicketTypesInGroup(groupId);
	const getOptions = useGetRequestFormOptions(groupId);

	return (
		<Wrapper>
			{shouldShowSelect ? (
				<Select
					id="servicedesk-portal-settings-request-groups.ui.portal-group.request-form.select"
					autoFocus
					openMenuOnFocus
					controlShouldRenderValue={false}
					closeMenuOnSelect={false}
					placeholder={formatMessage(messages.selectRequestForm)}
					getOptionLabel={getOptionLabel}
					options={getOptions()}
					minMenuHeight={250}
					// @ts-expect-error - TS2339 - Property 'id' does not exist on type 'TicketType | OptionsType<TicketType> | null'.
					onChange={({ id }) => {
						add(id);
					}}
					onInputChange={(_: string, { action }: InputActionMeta) => {
						action === 'menu-close' && setShowMenu(false);
					}}
					components={{ Option, MenuList }}
					// @ts-expect-error - TS2322 - Type '{ readonly menu: (provided: CSSStyleDeclaration) => { zIndex: number; accentColor: string; alignContent: string; alignItems: string; alignSelf: string; alignmentBaseline: string; all: string; animation: string; animationDelay: string; ... 443 more ...; setProperty(property: string, value: string | null, priority?: s...' is not assignable to type 'Partial<Styles<TicketType, boolean, GroupTypeBase<TicketType>>>'.
					styles={selectStyles}
				/>
			) : (
				<span>
					<Button
						testId="servicedesk-portal-settings-request-groups.ui.portal-group.request-form.button"
						appearance="link"
						spacing="none"
						shouldFitContainer={false}
						onClick={(_, analyticEvent) => {
							fireUIAnalytics(analyticEvent, 'addRequestForm');
							setShowMenu(true);
						}}
					>
						<>
							{}
							{/* @ts-expect-error - TS2769 - No overload matches this call. */}
							<EditorAddIcon size="small" />
							{formatMessage(messages.addRequestForm)}
						</>
					</Button>
				</span>
			)}
		</Wrapper>
	);
};

export default RequestForm;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div({
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'center',
	margin: `0 ${token('space.300', '24px')}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${gridSize * 5}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SelectItem = styled.div<{
	ref: (instance: HTMLDivElement | null) => void;
	isFocused: boolean;
}>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	backgroundColor: ({ isFocused }) =>
		isFocused
			? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				token('color.background.neutral.subtle.pressed', colors.N20)
			: // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				token('color.background.neutral.subtle', colors.N0),
	cursor: 'pointer',
});

const selectStyles = {
	menu: (provided: CSSStyleDeclaration) => ({
		...provided,
		zIndex: 2,
	}),
} as const;
